// SECAO "Guia de tamanhos" da ficha do produto (docs/specs/2026-09-23-guia-
// de-tamanhos-design.md secao 6), logo depois de "Instruções de lavagem".
// Diferente das tecnicidades/lavagem (chips N-a-N), aqui e 0..1 por produto
// com heranca por CATEGORIA — por isso o controle e um seletor de 3 vias
// (Herdar da categoria | guia ativo | Nenhum), nao uma grade de chips.
// Chave "Sobrescrever no canal" no mesmo padrao das outras secoes com
// override (BaseProductPanel: campo base + override do canal).
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Info } from "lucide-react";
import { api, apiErrorMessage } from "../lib/api";
import type { ProductDetail, SizeGuideOption } from "../lib/types";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";
import { EmptyState } from "./ui/empty-state";

interface SizeGuideTabProps {
  product: ProductDetail;
  slug: string;
  disabled: boolean;
}

const NONE_VALUE = "__none__";
const INHERIT_VALUE = "__inherit__";

function originLabel(
  source: "channel" | "product" | "category" | "none",
  fromCategory: { category_name: string } | null,
): string {
  switch (source) {
    case "channel":
      return "Só neste canal";
    case "product":
      return "Escolhido neste produto";
    case "category":
      return fromCategory ? `Vem da categoria ${fromCategory.category_name}` : "Vem da categoria";
    case "none":
    default:
      return "Sem guia";
  }
}

export function SizeGuideTab({ product, slug, disabled }: SizeGuideTabProps): JSX.Element {
  const qc = useQueryClient();
  const queryKey = ["product", slug, product.sku];

  const { data: options = [], isLoading, isError, error } = useQuery<SizeGuideOption[]>({
    queryKey: ["size-guides-active"],
    // GET /api/size-guides devolve { items: [...] }, mesmo formato de
    // /api/care-instructions e /api/technicalities.
    queryFn: async () => (await api.get<{ items: SizeGuideOption[] }>("/api/size-guides")).data.items,
  });

  const sg = product.size_guide ?? {
    product: { set: false, size_guide_id: null },
    channel: { set: false, size_guide_id: null },
    from_category: null,
    effective: null,
    effective_source: "none" as const,
  };

  if (isLoading) {
    return (
      <div className="stack">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }
  if (isError) {
    return <EmptyState title="Erro ao carregar guias de tamanho" description={apiErrorMessage(error)} />;
  }

  return (
    <div className="stack" style={{ gap: "var(--sp-4)" }}>
      <div
        className="cluster"
        style={{
          alignItems: "flex-start",
          borderRadius: "var(--radius-md)",
          background: "var(--color-soft)",
          padding: "var(--sp-3)",
        }}
      >
        <Info className="icon" style={{ marginTop: ".125rem" }} aria-hidden />
        <div className="field__hint">
          Guia efetivo: <b className="text-ink">{sg.effective?.title ?? "Sem guia"}</b> —{" "}
          {originLabel(sg.effective_source, sg.from_category)}. Sem escolha aqui, o produto usa o guia
          da categoria mais específica dele.
        </div>
      </div>

      <SizeGuideSelector
        title="Guia deste produto"
        hint="Vale para todos os canais, a menos que o canal sobrescreva abaixo."
        current={sg.product}
        options={options}
        disabled={disabled}
        onSave={async (mode, sizeGuideId) => {
          await api.put(`/api/channels/${slug}/products/${product.sku}/size-guide`, mode);
          toast.success(
            mode.mode === "inherit"
              ? "Voltou a herdar o guia da categoria."
              : sizeGuideId === null
                ? "Produto marcado como sem guia de tamanhos."
                : "Guia de tamanhos do produto salvo.",
          );
          void qc.invalidateQueries({ queryKey });
        }}
      />

      <div className="border-t border-line" />

      <SizeGuideSelector
        title={`Sobrescrever no canal "${slug}"`}
        hint="Só afeta este canal — os outros continuam com o guia do produto."
        current={sg.channel}
        options={options}
        disabled={disabled}
        collapsible
        onSave={async (mode, sizeGuideId) => {
          await api.put(`/api/channels/${slug}/products/${product.sku}/size-guide/override`, mode);
          toast.success(
            mode.mode === "inherit"
              ? `Override removido: canal "${slug}" volta a usar o guia do produto.`
              : sizeGuideId === null
                ? `Canal "${slug}" marcado como sem guia de tamanhos.`
                : `Guia de tamanhos do canal "${slug}" salvo.`,
          );
          void qc.invalidateQueries({ queryKey });
        }}
      />

      {sg.effective ? (
        <SizeGuidePreview
          columns={sg.effective.columns}
          rows={sg.effective.rows}
          unit={sg.effective.unit}
          imageUrl={sg.effective.image_url}
          notes={sg.effective.notes}
        />
      ) : (
        <EmptyState
          title="Sem guia de tamanhos"
          description="Este produto (neste canal) não tem guia de tamanhos efetivo."
        />
      )}
    </div>
  );
}

interface Selection {
  set: boolean;
  size_guide_id: number | null;
}

type SaveMode = { mode: "inherit" } | { mode: "set"; size_guide_id: number | null };

function selectionToValue(sel: Selection): string {
  if (!sel.set) return INHERIT_VALUE;
  if (sel.size_guide_id === null) return NONE_VALUE;
  return String(sel.size_guide_id);
}

function SizeGuideSelector({
  title,
  hint,
  current,
  options,
  disabled,
  collapsible,
  onSave,
}: {
  title: string;
  hint: string;
  current: Selection;
  options: SizeGuideOption[];
  disabled: boolean;
  collapsible?: boolean;
  onSave: (mode: SaveMode, sizeGuideId: number | null) => Promise<void>;
}): JSX.Element {
  const [open, setOpen] = useState(!collapsible || current.set);
  const [value, setValue] = useState(selectionToValue(current));
  useEffect(() => {
    setValue(selectionToValue(current));
    setOpen(!collapsible || current.set);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current.set, current.size_guide_id]);

  const mutation = useMutation({
    mutationFn: async () => {
      let mode: SaveMode;
      let sizeGuideId: number | null = null;
      if (value === INHERIT_VALUE) {
        mode = { mode: "inherit" };
      } else if (value === NONE_VALUE) {
        mode = { mode: "set", size_guide_id: null };
      } else {
        sizeGuideId = Number(value);
        mode = { mode: "set", size_guide_id: sizeGuideId };
      }
      await onSave(mode, sizeGuideId);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const dirty = value !== selectionToValue(current);

  const body = (
    <div className="stack" style={{ gap: ".5rem" }}>
      <select
        className="input"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={disabled}
      >
        <option value={INHERIT_VALUE}>Herdar da categoria</option>
        <option value={NONE_VALUE}>Nenhum (sem guia)</option>
        {options.map((o) => (
          <option key={o.id} value={String(o.id)}>
            {o.title}
          </option>
        ))}
      </select>
      {!disabled ? (
        <div className="flex justify-end">
          <Button size="sm" onClick={() => mutation.mutate()} loading={mutation.isPending} disabled={!dirty}>
            Salvar
          </Button>
        </div>
      ) : null}
    </div>
  );

  if (!collapsible) {
    return (
      <section>
        <div className="spread" style={{ marginBottom: ".5rem" }}>
          <h3 className="row__title">{title}</h3>
        </div>
        <p className="field__hint" style={{ marginBottom: ".5rem" }}>
          {hint}
        </p>
        {body}
      </section>
    );
  }

  return (
    <section>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="cluster"
        style={{ width: "100%", textAlign: "left" }}
      >
        <h3 className="row__title">{title}</h3>
        <span className="chip">{current.set ? "lista própria" : "usa o produto"}</span>
      </button>
      {open ? (
        <div className="stack" style={{ gap: ".5rem", marginTop: ".5rem" }}>
          <p className="field__hint">{hint}</p>
          {body}
        </div>
      ) : null}
    </section>
  );
}

function SizeGuidePreview({
  columns,
  rows,
  unit,
  imageUrl,
  notes,
}: {
  columns: string[];
  rows: { size: string; values: string[] }[];
  unit: string;
  imageUrl: string | null;
  notes: string;
}): JSX.Element {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
      <div style={{ overflowX: "auto", maxWidth: "100%" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th
                style={{
                  padding: ".375rem .5rem",
                  textAlign: "left",
                  fontSize: "var(--text-sm)",
                  fontWeight: 800,
                  color: "var(--color-ink-2)",
                  borderBottom: ".0625rem solid var(--color-line)",
                }}
              >
                Tamanho
              </th>
              {columns.map((c, i) => (
                <th
                  key={i}
                  style={{
                    padding: ".375rem .5rem",
                    textAlign: "left",
                    fontSize: "var(--text-sm)",
                    fontWeight: 800,
                    color: "var(--color-ink-2)",
                    borderBottom: ".0625rem solid var(--color-line)",
                  }}
                >
                  {c} ({unit})
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr key={ri}>
                <td style={{ padding: ".375rem .5rem", borderBottom: ".0625rem solid var(--color-line)", fontWeight: 800 }}>
                  {r.size}
                </td>
                {r.values.map((v, ci) => (
                  <td key={ci} style={{ padding: ".375rem .5rem", borderBottom: ".0625rem solid var(--color-line)" }}>
                    {v || "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {notes ? <p className="field__hint" style={{ marginTop: ".5rem" }}>{notes}</p> : null}
      </div>
      {imageUrl ? (
        <div
          className="rounded-md border border-line"
          style={{
            width: "7rem",
            height: "7rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            background: "var(--color-soft)",
            flexShrink: 0,
          }}
        >
          <img src={imageUrl} alt="Foto de medição" className="h-full w-full object-contain" />
        </div>
      ) : null}
    </div>
  );
}
